import { z } from "zod";

// TES-1306 — the patient's optional backup phone number for a care meeting.
//
// Emelie, 2026-10-13: after a technical problem in a care meeting, the
// patient may leave a number the doctor can call if the video does not
// connect. It is asked only after payment, never required, and purpose
// limited: "Numret är ändamålsbegränsat till reservkontakt vid tekniska
// problem och får inte användas för marknadsföring."
//
// Who sees it: the patient herself (patient-api/v1/me/backup-phone) and the
// treating doctor, next to the call in the meeting workspace and the meeting
// calendar. Not the coach, not the admin's read-only support views, and no
// outbound integration (mail, analytics, AI assist) reads the column.

/** GET/PUT patient-api/v1/me/backup-phone. `null` = none left. */
export const patientBackupPhoneSchema = z.object({
  backupPhone: z.string().nullable(),
});
export type PatientBackupPhone = z.infer<typeof patientBackupPhoneSchema>;

/** PUT body. The server normalises; the client sends what she typed. */
export const setPatientBackupPhoneSchema = z.object({
  backupPhone: z.string().max(40),
});

/**
 * Normalise a phone number to E.164 (`+46701234567`), or `null` when it is
 * not one.
 *
 * Accepted: Swedish national numbers (`070-123 45 67`, `08-123 456 78`),
 * `+46…` with or without the trunk zero (`+46 (0)70…`), and international
 * numbers written with `+` or `00`. Separators (spaces, hyphens, dots,
 * parentheses) are ignored. A number without a country prefix is read as
 * Swedish, because a national number is what a Swedish patient types.
 *
 * Shared by the API (the authority) and the app (to say "check the number"
 * before a round trip).
 */
export function normalizeBackupPhone(input: string): string | null {
  const trimmed = input.trim();
  if (!/^[+\d][\d\s().-]*$/.test(trimmed)) return null;
  let digits = trimmed.replace(/[\s().-]/g, "");
  if (digits.startsWith("00")) digits = `+${digits.slice(2)}`;

  if (digits.startsWith("+46")) {
    // "+46 (0)70 …" — the trunk zero some people keep after the country code.
    const national = digits.slice(3).replace(/^0/, "");
    return /^[1-9]\d{6,8}$/.test(national) ? `+46${national}` : null;
  }
  if (digits.startsWith("+")) {
    // E.164: at most 15 digits, country codes never start with 0. Eight is
    // the shortest number worth calling across a border.
    return /^\+[1-9]\d{7,14}$/.test(digits) ? digits : null;
  }
  // Swedish national form: trunk zero, then 7–9 digits (area code included).
  return /^0[1-9]\d{6,8}$/.test(digits) ? `+46${digits.slice(1)}` : null;
}
